import { ArrowLeft, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ButtonComponent } from "../components/ui/buttonComponent";
import DropDownMenuComponent from "../components/DropDownMenu";
import { rangeSelector } from "../utils/rangeSelector";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSurveyFilters } from "../hooks/fetching/getSurveyFilters";
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { SurveyInfo, SurveyQuestion } from "../types/defaultTypes";
import { usePostSurvey } from "../hooks/mutations/post-survey";
import { getSurveyDetails, type Survey } from "../hooks/fetching/getSurvey";
import { statusSelect } from "./SurveysPage";
import { validateSurvey, type ValidationError } from "../utils/validateSurvey";
import SelectTab from "../components/SelectTabSurvey";
import { getSurveyUsers, type SurveyUser } from "../hooks/fetching/getSurveyUser";
import { Table, type Column } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { useDebouncedValue } from "../utils/useDebouncedValue";
import { useSendManualSurvey } from "../hooks/mutations/post-manual-survey";
import { useToast } from "../context/ToasterContext";
import { useTranslation } from "react-i18next";

const SurveyManage = () => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
  });
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"manage" | "email" | "users">("manage");
  const [selectedUsers, setSelectedUsers] = useState<{ id: number; email: string }[]>([]);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(0);
  const [selectedCourse, setSelectedCourse] = useState(0);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [formErrors, setFormErrors] = useState<ValidationError | null>(null);
  const [orgOptions, setOrgOptions] = useState<{ option: string; label: string; courses?: string[] }[]>([
    { option: "", label: "Organizations" },
  ]);
  const [courseOptions, setCourseOptions] = useState<{ option: string; label: string }[]>([
    { option: "", label: "Courses" },
  ]);
  const [surveyInfo, setSurveyInfo] = useState<SurveyInfo>({
    id: null as number | null,
    title: "",
    description: "",
    surveyTrigger: "",
    autoSend: false,
    includeUsers: false,
  });
  const [email, setEmail] = useState({
    header: "Hi {{name}},",
    subheader: "You have a new survey waiting!",
    text: "Your feedback helps improve our training programs.",
    subject: "Your feedback on {{courseTitle}}",
  });

  /* ---------- Queries ---------- */
  const { data, isFetching } = useQuery({
    queryKey: ["surveyFilters"],
    queryFn: () => getSurveyFilters(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const { data: survey, isFetching: isFetchingSurvey } = useQuery({
    queryKey: ["surveyDetails", orgOptions[selectedOrg]?.option, courseOptions[selectedCourse]?.option?.toLowerCase()],
    queryFn: () =>
      getSurveyDetails(orgOptions[selectedOrg]?.option, courseOptions[selectedCourse]?.option?.toLowerCase()),
    enabled: selectedOrg > 0 && selectedCourse > 0,
  });

  const { data: surveyUsers, isFetching: isFetchingUsers } = useQuery({
    queryKey: [
      "surveyUsers",
      orgOptions[selectedOrg]?.option,
      courseOptions[selectedCourse]?.option?.toLowerCase(),
      pagination.page,
      pagination?.itemsPerPage,
      useDebouncedValue(search, 300),
    ],
    queryFn: () =>
      getSurveyUsers(
        orgOptions[selectedOrg]?.option,
        courseOptions[selectedCourse]?.option?.toLowerCase(),
        pagination.page,
        pagination?.itemsPerPage,
        search
      ),
    enabled: selectedOrg > 0 && selectedCourse > 0,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  /* ---------- Effects ---------- */
  useEffect(() => {
    if (!isFetchingSurvey) {
      if (survey) {
        setSurveyInfo({
          id: survey.id,
          title: survey?.title ?? "",
          description: survey?.description ?? "",
          autoSend: survey?.auto_send ?? false,
          surveyTrigger: survey?.send_delay?.toString() ?? "",
          includeUsers: survey?.include_previous ?? false,
        });
        setEmail({
          header: survey?.email_header ?? "Hi {{name}},",
          subheader: survey?.email_subheader ?? "You have a new survey waiting!",
          text: survey?.email_body ?? "Your feedback helps improve our training programs.",
          subject: survey?.subject ?? "Your feedback on {{courseTitle}}",
        });
        setQuestions((survey?.survey_json as any) ?? []);
      } else {
        setSurveyInfo({
          id: null,
          title: "",
          description: "",
          autoSend: false,
          surveyTrigger: "",
          includeUsers: false,
        });
        setEmail({
          header: "Hi {{name}},",
          subheader: "You have a new survey waiting!",
          text: "Your feedback helps improve our training programs.",
          subject: "Your feedback on {{courseTitle}}",
        });
        setQuestions([]);
      }
    }
  }, [survey, isFetchingSurvey, selectedOrg, selectedCourse]);

  useEffect(() => {
    if (data && !isFetching) {
      const orgs = [
        { option: "", label: "Organizations", courses: [] },
        ...data.organizations?.map((item) => ({
          option: String(item.id),
          label: item.name,
          courses: item.courses ?? [],
        })),
      ];
      setOrgOptions(orgs);

      if (searchParams.get("id")) {
        const index = orgs.findIndex((org) => Number(org.option) === Number(searchParams.get("id"))) ?? 0;
        const courses = [
          { option: "", label: "Courses" },
          ...(orgs[index]?.courses || []).map((item) => ({
            label: item.toLowerCase(),
            option: item.toLowerCase(),
          })),
        ];
        setSelectedOrg(index);
        setCourseOptions(courses);
        if (searchParams.get("courseName") && index !== 0) {
          setSelectedCourse(
            courses.findIndex((org) => org.option.toLowerCase() === searchParams.get("courseName")?.toLowerCase()) ?? 0
          );
        }
      }
    }
  }, [data, isFetching]);

  /* ---------- Mutation ---------- */
  const surveyPost = usePostSurvey({
    onSuccess: (res) => {
      setSurveyInfo({
        id: res.id,
        title: res.title ?? "",
        description: res.description ?? "",
        surveyTrigger: res.send_delay?.toString() ?? "",
        autoSend: res.auto_send ?? false,
        includeUsers: res.include_previous ?? false,
      });
      queryClient.setQueryData(
        ["surveyDetails", orgOptions[selectedOrg]?.option, courseOptions[selectedCourse]?.option?.toLowerCase()],
        (old: Survey) => (old ? { ...old, status: res.status } : old)
      );
      setQuestions((res.survey_json as any) ?? []);
    },
    onFail: (error) => {
      showToast({
        header: t("constants.error_occured"),
        line: error?.response?.data?.message,
        error: true,
      });
    },
  });

  const sendEmail = useSendManualSurvey({
    onSuccess: (res) => {
      setSelectedUsers([]);
      showToast({
        header: "Emails sent!",
        error: false,
      });
    },
    onFail: (error) => {
      showToast({
        header: t("constants.error_occured"),
        line: error?.response?.data?.message,
        error: true,
      });
    },
  });

  /* ---------- Handlers ---------- */
  const handleSave = (status: "draft" | "active" | "stopped") => {
    setFormErrors(null);
    const validation = validateSurvey(selectedOrg, selectedCourse, surveyInfo, questions);
    if (
      validation.survey.description ||
      validation.survey.questions ||
      validation.survey.surveyTrigger ||
      validation.survey.title ||
      validation.questions.length
    ) {
      setFormErrors(validation);
      return;
    }

    surveyPost.mutate({
      id: surveyInfo.id ?? undefined,
      orgId: Number(orgOptions[selectedOrg]?.option),
      courseName: courseOptions[selectedCourse]?.option?.toLowerCase(),
      title: surveyInfo.title,
      description: surveyInfo.description,
      send_delay: Number(surveyInfo.surveyTrigger),
      status,
      surveyJson: questions,
      auto_send: surveyInfo.autoSend,
      include_previous: surveyInfo.includeUsers,
      subject: email.subject,
      email_header: email.header,
      email_subheader: email.subheader,
      email_body: email.text,
    });
  };

  const updateUrlParams = (params: Record<string, string | undefined>) => {
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    });
    window.history.replaceState({}, "", url.toString());
  };

  const columns: Column<any>[] = [
    {
      header: "name",
      accessor: (row) => (
        <>
          {row?.first_name} {row?.last_name}
        </>
      ),
    },
    {
      header: "email",
      accessor: (row) => (
        <a className="text-blue-500" href={`mailto:${row?.email}`}>
          {row?.email}
        </a>
      ),
    },
    {
      header: "	COMPLETED SESSIONS",
      accessor: "completed_sessions",
    },
    {
      header: "TOTAL SESSIONS",
      accessor: "total_sessions",
    },
  ];

  return (
    <div className="w-full h-full flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-8 duration-400 ease-out">
      <Link to={"/surveys"} className="text-base font-medium text-gray-500 flex items-center gap-1">
        <ArrowLeft className="h-[22px]" /> Back to Survey Management
      </Link>
      {/* Top controls */}
      <div className="bg-white rounded-2xl shadow p-8 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h1 className="font-bold text-2xl flex gap-2 items-center">
            {survey ? `${survey?.course_name} Survey for ${orgOptions[selectedOrg]?.label}` : "Create New Survey"}{" "}
          </h1>{" "}
          {statusSelect(survey?.status || "")}
        </div>
        <div className="flex gap-3 items-center">
          {["draft", "stopped", ""].includes(survey?.status || "") && (
            <ButtonComponent onClick={() => handleSave("draft")} className="!bg-input-bg !text-gray-500">
              Save Draft
            </ButtonComponent>
          )}
          {survey?.status === "active" && (
            <ButtonComponent onClick={() => handleSave("stopped")}>Stop Survey</ButtonComponent>
          )}
          {["draft", "active", "stopped", ""].includes(survey?.status || "") && (
            <ButtonComponent onClick={() => handleSave("active")}>Publish Survey</ButtonComponent>
          )}
        </div>
      </div>

      {/* Organization & Course Select */}
      {isFetching ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="animate-spin rounded-full self-center h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : (
        <div className="shadow flex flex-col gap-5 p-6 rounded-2xl bg-white">
          <h1 className="text-lg font-semibold text-gray-900">Select Organization & Course</h1>
          <div className="flex gap-4 items-center">
            <DropDownMenuComponent
              options={rangeSelector(orgOptions, (i) => {
                setSelectedOrg(i);
                setSelectedCourse(0);
                setCourseOptions([
                  { option: "", label: "Courses" },
                  ...(orgOptions[i]?.courses || []).map((item) => ({ label: item, option: item })),
                ]);
                updateUrlParams({ id: String(orgOptions[i]?.option) });
              })}
              selected={orgOptions[selectedOrg]?.label}
              buttonClass="py-[9px] px-[13px] rounded-xl bg-[#F8F9FA] !w-[260px]"
            />
            <DropDownMenuComponent
              disabled={selectedOrg <= 0}
              options={rangeSelector(courseOptions, (i) => {
                setSelectedCourse(i);
                updateUrlParams({
                  id: String(orgOptions[selectedOrg]?.option),
                  courseName: courseOptions[i]?.option,
                });
              })}
              selected={courseOptions[selectedCourse]?.label}
              buttonClass="py-[9px] px-[13px] rounded-xl bg-[#F8F9FA] !w-[260px]"
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex px-4 items-center gap-4">
        {[
          { key: "manage", label: "Manage Survey" },
          { key: "email", label: "Email Template" },
          { key: "users", label: "Users" },
        ].map((tabItem) => (
          <button
            key={tabItem.key}
            onClick={() => setTab(tabItem.key as any)}
            className={`relative text-lg font-semibold px-6 py-2 rounded-full transition-all duration-300 border-2 ${
              tab === tabItem.key
                ? "bg-xuron-orange text-white border-xuron-orange shadow-md"
                : "border-gray-300 text-gray-600 hover:border-xuron-orange hover:text-xuron-orange"
            }`}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {/* Email Config & Preview */}

      <SelectTab
        tab={tab}
        isFetching={isFetching}
        sensors={sensors}
        surveyInfo={surveyInfo}
        setSurveyInfo={setSurveyInfo}
        questions={questions}
        setQuestions={setQuestions}
        formErrors={formErrors}
        email={email}
        setEmail={setEmail}
      />

      {tab === "users" && (
        <div className="flex flex-col relative gap-2">
          <div className="flex justify-between relative items-center w-full">
            <Input
              value={search}
              divClass="!w-[50%]"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
            />
            <ButtonComponent
              onClick={() =>
                sendEmail.mutate({
                  survey_id: survey?.id,
                  users: selectedUsers,
                })
              }
            >
              {/* selectedUsers */}
              Send Emails
            </ButtonComponent>
          </div>

          <div className="flex flex-col gap-3">
            {selectedUsers?.length > 0 && (
              <div className="flex flex-wrap w-full gap-2.5 gap-y-2.5">
                {selectedUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`px-2.5 py-1 bg-gray-200 text-gray-800 rounded-xl w-fit flex items-center gap-1`}
                  >
                    {user.email}
                    <button
                      type="button"
                      onClick={() => setSelectedUsers((prev) => prev.filter((o) => o.id !== user.id))}
                      className="hover:text-red-500 transition-colors duration-150"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Table
            data={surveyUsers?.data ?? []}
            columns={columns}
            isLoading={isFetchingUsers}
            totalPages={surveyUsers?.total_pages || 0}
            pagination={pagination}
            setPagination={setPagination}
            wrapperClassName="min-h-[551px]"
            totalRecords={surveyUsers?.total_records ?? pagination?.itemsPerPage * 1}
            selectedRows={selectedUsers}
            setSelectedRows={setSelectedUsers}
            // onRowSelect={(rows) => console.log("Selected users:", rows)} // optional
            onRowAction={(row) => {
              const orgId = orgOptions?.[selectedOrg]?.option;
              const course = courseOptions?.[selectedCourse]?.option;
              const backto = encodeURIComponent(
                `/surveys/manage?id=${orgId ?? ""}&courseName=${course?.toLowerCase() ?? ""}`
              );

              navigate(`/user?id=${row.id}&backto=${backto}`);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SurveyManage;
